class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
    
    // JAMIE CUSTOM: Check if SKU sorting is enabled for wholesale quick order section
    if (this.getAttribute('data-sku-sorting') === 'true') {
      this.initializeSkuSorting();
    }
  }
  
  // JAMIE CUSTOM: Initialize SKU sorting for wholesale quick order section
  initializeSkuSorting() {
    // Check if we're on a page with the wholesale product grid
    const productGrid = document.getElementById('wholesale-product-grid');
    if (!productGrid) {
      return;
    }
    
    // Add SKU options to sort selects
    this.addSkuOptionsToSortSelects();
    
    // Check if we need to sort by SKU on page load
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');
    
    if (sortBy === 'sku-ascending' || sortBy === 'sku-descending') {
      this.sortProductsBySku(sortBy);
    } else if (!sortBy) {
      // Default to SKU ascending if no sort parameter is present
      this.sortProductsBySku('sku-ascending');
      
      // Update URL without page reload
      const url = new URL(window.location);
      url.searchParams.set('sort_by', 'sku-ascending');
      window.history.pushState({}, '', url);
    }
    
    // Set up observer to detect when the product grid changes
    this.setupProductGridObserver();
  }
  
  // JAMIE CUSTOM: Add SKU options to sort selects for wholesale quick order section
  addSkuOptionsToSortSelects() {
    // Find all sort selects
    const sortSelects = document.querySelectorAll('select[name="sort_by"]');
    
    sortSelects.forEach(select => {
      // Check if SKU options already exist
      const existingSkuOptions = select.querySelectorAll('option[value="sku-ascending"], option[value="sku-descending"]');
      if (existingSkuOptions.length > 0) {
        return;
      }
      
      // Create SKU options
      const skuAscendingOption = document.createElement('option');
      skuAscendingOption.value = 'sku-ascending';
      skuAscendingOption.textContent = 'SKU: Ascending';
      
      const skuDescendingOption = document.createElement('option');
      skuDescendingOption.value = 'sku-descending';
      skuDescendingOption.textContent = 'SKU: Descending';
      
      // Add SKU options to select
      select.appendChild(skuAscendingOption);
      select.appendChild(skuDescendingOption);

      // Set default selected option to SKU ascending if no sort parameter is present
      const urlParams = new URLSearchParams(window.location.search);
      const sortBy = urlParams.get('sort_by');
      if (!sortBy) {
        select.value = 'sku-ascending';
      }
    });
  }
  
  // JAMIE CUSTOM: Set up a MutationObserver to detect when the product grid changes
  setupProductGridObserver() {
    const productGrid = document.getElementById('wholesale-product-grid');
    if (!productGrid) {
      return;
    }
    
    // Create a MutationObserver to watch for changes to the product grid
    const observer = new MutationObserver((mutations) => {
      // Check if the grid has been updated
      const gridUpdated = mutations.some(mutation => 
        mutation.type === 'childList' && mutation.target.id === 'wholesale-product-grid'
      );
      
      if (gridUpdated) {
        // Check if we need to sort by SKU
        const urlParams = new URLSearchParams(window.location.search);
        const sortBy = urlParams.get('sort_by');
        
        if (sortBy === 'sku-ascending' || sortBy === 'sku-descending') {
          // Wait a bit for the grid to fully update
          setTimeout(() => {
            this.sortProductsBySku(sortBy);
          }, 100);
        }
      }
    });
    
    // Start observing the product grid
    observer.observe(productGrid, { childList: true, subtree: true });
  }
  
  // JAMIE CUSTOM: Sort products by SKU for wholesale quick order section
  sortProductsBySku(sortValue) {
    // Prevent recursive sorting
    if (this.isSortingInProgress) {
      return;
    }
    
    this.isSortingInProgress = true;
    
    const productGrid = document.getElementById('wholesale-product-grid');
    if (!productGrid) {
      this.isSortingInProgress = false;
      return;
    }
    
    // Get all product items
    const productItems = Array.from(productGrid.querySelectorAll('.wholesale-grid__item'));
    
    if (productItems.length === 0) {
      this.isSortingInProgress = false;
      return;
    }
    
    // Get SKU values for each product
    const productsWithSku = productItems.map(item => {
      const skuElement = item.querySelector('.wholesale-card__sku');
      const sku = skuElement ? skuElement.textContent.trim() : '';
      return { item, sku };
    });
    
    // Sort products by SKU
    productsWithSku.sort((a, b) => {
      if (sortValue === 'sku-ascending') {
        return a.sku.localeCompare(b.sku);
      } else {
        return b.sku.localeCompare(a.sku);
      }
    });
    
    // Clear the product grid
    productGrid.innerHTML = '';
    
    // Add sorted products back to the grid
    productsWithSku.forEach(({ item }) => {
      productGrid.appendChild(item);
    });
    
    // Reset the flag after a short delay to ensure DOM updates are complete
    setTimeout(() => {
      this.isSortingInProgress = false;
    }, 500);
  }

  // JAMIE CUSTOM: Handle mobile sort change for wholesale quick order section
  handleMobileSortChange(sortValue) {
    // Update URL without page reload
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortValue);
    window.history.pushState({}, '', url);
    
    // Find the mobile sort form
    const mobileSortForm = document.querySelector('#FacetFiltersFormMobile');
    if (!mobileSortForm) {
      return;
    }
    
    // Find the sort select in the mobile form
    const sortSelect = mobileSortForm.querySelector('select[name="sort_by"]');
    if (!sortSelect) {
      return;
    }
    
    // Set the sort value
    sortSelect.value = sortValue;
    
    // Submit the form to trigger a page refresh with the SKU sort parameter
    mobileSortForm.submit();
    
    // Close the mobile menu drawer
    const mobileMenuDrawer = document.querySelector('.facets__mobile-drawer');
    if (mobileMenuDrawer) {
      const closeButton = mobileMenuDrawer.querySelector('.facets__mobile-drawer-close');
      if (closeButton) {
        closeButton.click();
      }
    }
  }

  // JAMIE CUSTOM: Flag to prevent recursive sorting
  isSortingInProgress = false;

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        
        // JAMIE CUSTOM: Check if we need to sort by SKU after the grid is updated
        const urlParams = new URLSearchParams(window.location.search);
        const sortBy = urlParams.get('sort_by');
        
        if (sortBy === 'sku-ascending' || sortBy === 'sku-descending') {
          // Find the facet form with SKU sorting enabled
          const facetForm = document.querySelector('facet-filters-form[data-sku-sorting="true"]');
          if (facetForm) {
            // Wait a bit for the grid to fully update
            setTimeout(() => {
              facetForm.sortProductsBySku(sortBy);
            }, 500);
          }
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    
    // JAMIE CUSTOM: Check if we need to sort by SKU after the grid is updated
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');
    
    if (sortBy === 'sku-ascending' || sortBy === 'sku-descending') {
      // Find the facet form with SKU sorting enabled
      const facetForm = document.querySelector('facet-filters-form[data-sku-sorting="true"]');
      if (facetForm) {
        // Wait a bit for the grid to fully update
        setTimeout(() => {
          facetForm.sortProductsBySku(sortBy);
        }, 500);
      }
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
    
    // JAMIE CUSTOM: Re-add SKU options to sort selects after filters are rendered
    const facetForm = document.querySelector('facet-filters-form[data-sku-sorting="true"]');
    if (facetForm) {
      facetForm.addSkuOptionsToSortSelects();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  // JAMIE CUSTOM: Handle form submission for wholesale quick order section
  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();
    const isSkuSorting = this.getAttribute('data-sku-sorting') === 'true';
    const sortBy = formData.get('sort_by');
    const isSkuSort = sortBy === 'sku-ascending' || sortBy === 'sku-descending';

    // JAMIE CUSTOM: If SKU sorting is enabled and we're sorting by SKU, sort client-side
    if (isSkuSorting && isSkuSort) {
      this.sortProductsBySku(sortBy);
      
      // Update URL without page reload
      const url = new URL(window.location);
      url.searchParams.set('sort_by', sortBy);
      window.history.pushState({}, '', url);
    } else {
      // Fix: Call the static renderPage method correctly
      FacetFiltersForm.renderPage(searchParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// JAMIE CUSTOM: Check for SKU sorting on page load for wholesale quick order section
document.addEventListener('DOMContentLoaded', function() {
  // Check for all facet-filters-form elements
  const allFacetForms = document.querySelectorAll('facet-filters-form');
  
  allFacetForms.forEach((form, index) => {
    // Check if this is a form with SKU sorting enabled
    if (form.getAttribute('data-sku-sorting') === 'true') {
      // Manually initialize SKU sorting if it hasn't been initialized yet
      if (!form.hasAttribute('data-sku-initialized')) {
        form.initializeSkuSorting();
        form.setAttribute('data-sku-initialized', 'true');
      }
    }
  });
  
  // Check if we have a wholesale product grid
  const productGrid = document.getElementById('wholesale-product-grid');
  if (productGrid) {
    // Check if we have a facet-filters-form with SKU sorting enabled
    const skuSortingForm = document.querySelector('facet-filters-form[data-sku-sorting="true"]');
    if (!skuSortingForm) {
      // If we don't have a form with SKU sorting enabled, but we have a wholesale product grid,
      // we should enable SKU sorting on the first facet-filters-form we find
      const firstFacetForm = document.querySelector('facet-filters-form');
      if (firstFacetForm) {
        firstFacetForm.setAttribute('data-sku-sorting', 'true');
        
        // Manually initialize SKU sorting
        if (!firstFacetForm.hasAttribute('data-sku-initialized')) {
          firstFacetForm.initializeSkuSorting();
          firstFacetForm.setAttribute('data-sku-initialized', 'true');
        }
      }
    }
  }
});

// JAMIE CUSTOM: Re-initialize SKU sorting when Shopify's AJAX navigation occurs
document.addEventListener('shopify:section:load', function(event) {
  // Check for all facet-filters-form elements
  const allFacetForms = document.querySelectorAll('facet-filters-form');
  
  allFacetForms.forEach((form, index) => {
    // Check if this is a form with SKU sorting enabled
    if (form.getAttribute('data-sku-sorting') === 'true') {
      // Manually initialize SKU sorting if it hasn't been initialized yet
      if (!form.hasAttribute('data-sku-initialized')) {
        form.initializeSkuSorting();
        form.setAttribute('data-sku-initialized', 'true');
      } else {
        // Add SKU options to sort selects
        form.addSkuOptionsToSortSelects();
        
        // Check if we need to sort by SKU on page load
        const urlParams = new URLSearchParams(window.location.search);
        const sortBy = urlParams.get('sort_by');
        
        if (sortBy === 'sku-ascending' || sortBy === 'sku-descending') {
          setTimeout(() => {
            form.sortProductsBySku(sortBy);
          }, 500);
        }
      }
    }
  });
  
  // Check if we have a wholesale product grid
  const productGrid = document.getElementById('wholesale-product-grid');
  if (productGrid) {
    // Check if we have a facet-filters-form with SKU sorting enabled
    const skuSortingForm = document.querySelector('facet-filters-form[data-sku-sorting="true"]');
    if (!skuSortingForm) {
      // If we don't have a form with SKU sorting enabled, but we have a wholesale product grid,
      // we should enable SKU sorting on the first facet-filters-form we find
      const firstFacetForm = document.querySelector('facet-filters-form');
      if (firstFacetForm) {
        firstFacetForm.setAttribute('data-sku-sorting', 'true');
        
        // Manually initialize SKU sorting
        if (!firstFacetForm.hasAttribute('data-sku-initialized')) {
          firstFacetForm.initializeSkuSorting();
          firstFacetForm.setAttribute('data-sku-initialized', 'true');
        }
      }
    }
  }
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
